import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export type VatClaimStatus = 'draft' | 'submitted' | 'partially_refunded' | 'refunded' | 'rejected';

export interface VatClaim {
  id: string;
  agent: string | null;
  country: string;
  periodYear: number;
  periodFrom: string | null;
  periodTo: string | null;
  claimed: number;
  ccy: string;
  submittedOn: string | null;
  reference: string | null;
  status: VatClaimStatus;
  refunded: number;
  refundedOn: string | null;
  rejected: number;
  agentFee: number;
  outstanding: number;
  notes: string | null;
}

export interface VatCountryRow {
  country: string;
  claimable: number;
  claimed: number;
  refunded: number;
  rejected: number;
  fee: number;
  outstanding: number;
  unclaimed: number;
  claims: number;
  missingDocuments: number;
  meetsAnnualMinimum: boolean;
}

export interface VatSummary {
  year: number;
  deadline: string;
  daysToDeadline: number;
  expired: boolean;
  ccy: string;
  totals: {
    claimable: number;
    claimed: number;
    refunded: number;
    rejected: number;
    fee: number;
    outstanding: number;
    unclaimed: number;
    net: number;
  };
  countries: VatCountryRow[];
  otherCurrencyClaims: number;
  notes: string[];
}

export function useVatSummary(year: number) {
  return useQuery({
    queryKey: ['vat', 'summary', year],
    queryFn: () => api.get<VatSummary>('/vat-refunds/summary', { year }),
  });
}

export function useVatClaims(year: number) {
  return useQuery({
    queryKey: ['vat', 'claims', year],
    queryFn: () => api.get<VatClaim[]>('/vat-refunds/claims', { year }),
  });
}

export type VatClaimInput = Omit<VatClaim, 'id' | 'outstanding' | 'status'> & { status?: VatClaimStatus };

export function useSaveVatClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: string; body: Partial<VatClaimInput> }) =>
      input.id
        ? api.patch<VatClaim>(`/vat-refunds/claims/${input.id}`, input.body)
        : api.post<VatClaim>('/vat-refunds/claims', input.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vat'] }),
  });
}

export function useDeleteVatClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/vat-refunds/claims/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vat'] }),
  });
}
